import type { CiTestResult } from "@/lib/types";
import { getPhaseTooltip } from "./phase-tooltips";
import { msFromSeconds, type Phase, WaterfallShell } from "./waterfall-core";

/**
 * Cascading Gantt-style waterfall: each phase starts where the previous one ended.
 * CRIU sub-phases are shown as nested bars within the CRIU Restore span.
 */

/** Labels that identify CRIU sub-phases (grouped under a parent "CRIU Restore" bar). */
const CRIU_SUB_PHASE_KEYWORDS = [
  "Init",
  "Fork",
  "Resource",
  "Memory",
  "Network",
  "Syscall",
  "CUDA",
];

function isCriuSubPhase(label: string): boolean {
  return CRIU_SUB_PHASE_KEYWORDS.some((keyword) => label.includes(keyword));
}

/** Assign colors to restore-span labels. */
function restoreSpanColor(label: string): string {
  if (label.includes("Init")) return "bg-green-300";
  if (label.includes("Fork")) return "bg-green-400";
  if (label.includes("Resource")) return "bg-emerald-400";
  if (label.includes("Memory")) return "bg-rose-400";
  if (label.includes("Network")) return "bg-purple-400";
  if (label.includes("Syscall")) return "bg-amber-400";
  if (label.includes("CUDA") && label.includes("Unlock")) return "bg-orange-400";
  if (label.includes("CUDA")) return "bg-orange-400";
  if (label.includes("CuMem") || label.includes("NCCL")) return "bg-cyan-500";
  if (label.includes("SymmMem") || label.includes("AllReduce")) return "bg-violet-500";
  if (label.includes("Wake")) return "bg-amber-500";
  if (label.includes("Inference")) return "bg-rose-400";
  return "bg-gray-400";
}

function buildPhases(result: CiTestResult): { phases: Phase[]; totalMs: number } {
  const phases: Phase[] = [];
  let cursor = 0;

  // Sequential pre-restore phases
  if (result.cold_start_ms != null && result.cold_start_ms > 0) {
    phases.push({
      label: "Cold Start",
      startMs: cursor,
      durationMs: result.cold_start_ms,
      color: "bg-slate-400",
    });
    cursor += result.cold_start_ms;
  }
  if (result.criu_dump_ms != null && result.criu_dump_ms > 0) {
    phases.push({
      label: "CRIU Dump",
      startMs: cursor,
      durationMs: result.criu_dump_ms,
      color: "bg-teal-500",
    });
    cursor += result.criu_dump_ms;
  }
  if (result.transfer_seconds != null && result.transfer_seconds > 0) {
    const transferMs = msFromSeconds(result.transfer_seconds);
    phases.push({
      label: "Transfer",
      startMs: cursor,
      durationMs: transferMs,
      color: "bg-blue-500",
    });
    cursor += transferMs;
  }

  const hasRestoreSpans = Array.isArray(result.restore_spans) && result.restore_spans.length > 0;

  if (hasRestoreSpans) {
    // --- Mode A: restore_spans available ---
    const restoreStartMs = cursor;
    // Safe to access — hasRestoreSpans guard above ensures non-null + non-empty
    const spans = result.restore_spans as NonNullable<typeof result.restore_spans>;

    // Partition spans into CRIU sub-phases and post-CRIU top-level phases
    const criuChildren: Phase[] = [];
    const postCriuPhases: Phase[] = [];
    let criuMaxEndMs = 0;
    let hasWakeUpSpan = false;

    for (const span of spans) {
      const durationMs = (span.end_s - span.start_s) * 1000;
      const startMs = restoreStartMs + span.start_s * 1000;
      const color = restoreSpanColor(span.label);

      if (span.label.toLowerCase().includes("wake")) {
        hasWakeUpSpan = true;
      }

      if (isCriuSubPhase(span.label)) {
        // Relative to the CRIU Restore parent phase
        criuChildren.push({
          label: span.label,
          startMs: span.start_s * 1000,
          durationMs,
          color,
        });
        criuMaxEndMs = Math.max(criuMaxEndMs, span.end_s * 1000);
      } else {
        postCriuPhases.push({
          label: span.label,
          startMs,
          durationMs,
          color,
        });
      }
    }

    // Add the parent CRIU Restore phase with children
    if (criuChildren.length > 0) {
      phases.push({
        label: "CRIU Restore",
        startMs: restoreStartMs,
        durationMs: criuMaxEndMs,
        color: "bg-green-500",
        children: criuChildren,
      });
    }

    // Add post-CRIU phases as top-level
    for (const phase of postCriuPhases) {
      phases.push(phase);
    }

    // Advance cursor past all spans
    const maxEndS = Math.max(...spans.map((span) => span.end_s));
    cursor = restoreStartMs + maxEndS * 1000;

    // Backward compat: still render wake_up_ms if no wake-up span in restore_spans
    if (!hasWakeUpSpan && result.wake_up_ms != null && result.wake_up_ms > 0) {
      phases.push({
        label: "Wake Up",
        startMs: cursor,
        durationMs: result.wake_up_ms,
        color: "bg-amber-500",
      });
      cursor += result.wake_up_ms;
    }

    // Backward compat: still render inference_ms if no inference span in restore_spans
    const hasInferenceSpan = spans.some((span) => span.label.toLowerCase().includes("inference"));
    if (!hasInferenceSpan && result.inference_ms != null && result.inference_ms > 0) {
      phases.push({
        label: "Inference",
        startMs: cursor,
        durationMs: result.inference_ms,
        color: "bg-rose-400",
      });
      cursor += result.inference_ms;
    }
  } else {
    // --- Mode B: no restore_spans (backward compat) ---

    // CRIU Restore with cascading sub-phases
    if (result.criu_restore_ms != null && result.criu_restore_ms > 0) {
      const subPhases: Phase[] = [];
      if (result.criu_phases) {
        let subCursor = 0;
        const subPhaseSpecs: [string, number, string][] = [
          ["Init + Image Read", msFromSeconds(result.criu_phases.init_s), "bg-green-300"],
          ["Process Fork", msFromSeconds(result.criu_phases.fork_s), "bg-green-400"],
          ["Resource Restore", msFromSeconds(result.criu_phases.resources_s), "bg-emerald-400"],
          ["Memory Remap", msFromSeconds(result.criu_phases.memory_remap_s), "bg-rose-400"],
          ["Network Unlock", msFromSeconds(result.criu_phases.network_unlock_s), "bg-purple-400"],
          ["Syscall Injection", msFromSeconds(result.criu_phases.syscall_inject_s), "bg-amber-400"],
          ["CUDA Restore", msFromSeconds(result.criu_phases.cuda_restore_s), "bg-orange-400"],
        ];
        for (const [label, duration, color] of subPhaseSpecs) {
          if (duration > 0) {
            subPhases.push({ label, startMs: subCursor, durationMs: duration, color });
            subCursor += duration;
          }
        }
      }
      phases.push({
        label: "CRIU Restore",
        startMs: cursor,
        durationMs: result.criu_restore_ms,
        color: "bg-green-500",
        children: subPhases.length > 0 ? subPhases : undefined,
      });
      cursor += result.criu_restore_ms;
    }

    // http_ready_ms is wall-clock from container launch → HTTP 200, overlapping
    // with criu_restore_ms. The gap covers CUDA re-init + uvicorn resume + polling.
    if (result.http_ready_ms != null && result.http_ready_ms > 0) {
      const criuRestoreMs = result.criu_restore_ms ?? 0;
      const httpPollingMs = result.http_ready_ms - criuRestoreMs;
      if (httpPollingMs > 0) {
        phases.push({
          label: "HTTP Polling",
          startMs: cursor,
          durationMs: httpPollingMs,
          color: "bg-amber-500",
        });
        cursor += httpPollingMs;
      }
    }

    if (result.wake_up_ms != null && result.wake_up_ms > 0) {
      phases.push({
        label: "Wake Up",
        startMs: cursor,
        durationMs: result.wake_up_ms,
        color: "bg-amber-500",
      });
      cursor += result.wake_up_ms;
    }

    if (result.inference_ms != null && result.inference_ms > 0) {
      phases.push({
        label: "Inference",
        startMs: cursor,
        durationMs: result.inference_ms,
        color: "bg-rose-400",
      });
      cursor += result.inference_ms;
    }
  }

  // Attach tooltips from the description map (case-insensitive lookup)
  for (const phase of phases) {
    phase.tooltip = getPhaseTooltip(phase.label);
    if (phase.children) {
      for (const child of phase.children) {
        child.tooltip = getPhaseTooltip(child.label);
      }
    }
  }

  return { phases, totalMs: cursor };
}

/** Map failure_phase values to waterfall phase labels. */
const failurePhaseToLabel: Record<string, string> = {
  start: "Cold Start",
  dump: "CRIU Dump",
  transfer: "Transfer",
  restore: "CRIU Restore",
  wake_up: "Wake Up",
  inference: "Inference",
};

export function TimingWaterfall({
  result,
  failurePhase,
}: {
  result: CiTestResult;
  failurePhase?: string | null;
}) {
  const { phases, totalMs } = buildPhases(result);
  const failedLabel = failurePhase ? (failurePhaseToLabel[failurePhase] ?? null) : null;

  return (
    <WaterfallShell
      phases={phases}
      totalMs={totalMs}
      failedLabel={failedLabel}
      totalRestoreMs={result.total_restore_ms}
    />
  );
}
